// Copyright 2016 Sorama B.V.

import { AudioStreamProperties, MeasurementStatus, VideoStreamProperties } from "datatypes/SoramaLocalApi";
import { StreamMeasurementServiceError } from "errors/StreamMeasurementServiceError";

export abstract class IStreamMeasurementService {
    /**
     * Indicates the state of the current measurement
     */
    public readonly measurementState: { key: string; state: MeasurementStatus }[];

    /**
     * Indicates wether a stream measurement is currently active in this class
     */
    public isStreamMeasurementActive: boolean;

    /**
     * Creates a new stream measurement with the provided properties.
     *  NOTE: If this call rejects no measurement will be active.
     * @param properties Properties for the stream measurement
     * @param onError Callback when an error occures keeping the measurement alive. When an error occurs the measurement is automatically cancelled.
     * @exception StreamMeasurementServiceError Unable to create a new stream measurement, see message for detail
     */
    public abstract create(
        properties: (AudioStreamProperties | VideoStreamProperties)[],
        onError: (e: StreamMeasurementServiceError) => void
    ): Promise<string[]>;

    /**
     * Cancels the current stream measurement
     * @exception StreamMeasurementServiceError No active stream measurement to cancel
     */
    public abstract cancel(): Promise<void>;

    /**
     * Record a portion of the current stream measurement to a storage container
     * @param storageId The storage container id to store the recording in, note that this container may not already contain a recording of the same kind.
     * @param startMS Start of the recording in milliseconds with respect to the current stream index, can be negative for recording data which is still buffered
     * @param durationMS Duration of the recording in milliseconds
     * @param progressCallback (Optional) Callback method to call each time the recording recieves a progress report from the server
     * @exception StreamMeasurementServiceError An error occurred while recording
     */
    public abstract record(
        storageId: string,
        startMS: number,
        durationMS: number,
        progressCallback?: (progress: number) => void
    ): Promise<void>;
}
